import { motion } from "framer-motion";
import { UserCheck, FileSearch, Coins, LineChart } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/shared";
import { BOARDROOM } from "@/data";

const STEPS = [
  { icon: UserCheck, title: "Verify", desc: "Complete institutional-grade KYC/KYB and accreditation in minutes." },
  { icon: FileSearch, title: "Explore", desc: "Review audited, tokenized real-world assets with full disclosures." },
  { icon: Coins, title: "Invest", desc: "Allocate capital on-chain with instant settlement and clear ownership." },
  { icon: LineChart, title: "Grow", desc: "Track performance, receive distributions and trade on secondary markets." },
];

const ROADMAP = [
  { q: "Q1", title: "Protocol Launch", desc: "AUR token generation event and core issuance rails go live." },
  { q: "Q2", title: "Secondary Markets", desc: "Compliant peer-to-peer trading across supported jurisdictions." },
  { q: "Q3", title: "Multi-Asset Expansion", desc: "Private credit, commodities and treasuries onboarded at scale." },
  { q: "Q4", title: "Global Passporting", desc: "Cross-border distribution to 60+ jurisdictions with local compliance." },
];

export function InvestmentProcess() {
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="investment-process">
      <SectionHeader chapter="07" kicker="How It Works" title="From verification to returns in four steps" center />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {STEPS.map((s, i) => (
          <Reveal key={s.title} i={i}>
            <div className="glass rounded-2xl p-8 h-full relative" data-testid={`process-step-${i}`}>
              <span className="font-display italic text-white/10 text-5xl absolute top-4 right-6">{i + 1}</span>
              <s.icon className="w-7 h-7 text-auvra-blue mb-6" />
              <h3 className="text-lg font-medium mb-3">{s.title}</h3>
              <p className="text-secondary-auvra text-sm leading-relaxed">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Leadership() {
  return (
    <section className="container-auvra py-24 md:py-32" data-testid="leadership">
      <SectionHeader chapter="08" kicker="Inside AUVRA" title="Built by an institutional team" subtitle="Decades of combined experience across capital markets, compliance and blockchain engineering — aligned behind one standard of trust." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {BOARDROOM.map((src, i) => (
          <Reveal key={src} i={i}>
            <motion.div whileHover={{ y: -6 }} className="rounded-2xl overflow-hidden border border-white/10 group">
              <img src={src} alt="AUVRA leadership" className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Roadmap() {
  return (
    <section className="bg-auvra-surface border-y border-white/10 py-24 md:py-32" data-testid="roadmap">
      <div className="container-auvra">
        <SectionHeader chapter="09" kicker="Roadmap" title="The path ahead" center />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {ROADMAP.map((r, i) => (
            <Reveal key={r.q} i={i}>
              <div className="border border-white/10 rounded-2xl p-8 h-full hover:border-auvra-blue/40 transition-colors" data-testid={`roadmap-${i}`}>
                <span className="font-mono text-auvra-blue text-sm">{r.q}</span>
                <h3 className="text-lg font-medium mt-3 mb-2">{r.title}</h3>
                <p className="text-secondary-auvra text-sm leading-relaxed">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
